import { TokenSymbol, Wei } from '../../types'
import { useTotalTransferableAvailableMarginValue } from '../perp/useTotalAvailableMarginValue'
import {
  useCurrencyByName,
  useExternalPriceByName,
} from '../../store/market/hooks'
import { useCurrencyBalance } from '../../store/account/hooks'
import { useMemo } from 'react'
import { bn, e2w, isZero } from '../../utils/number'
import { Pow18 } from '../../config'

export function useSpotTradeAvailableBalance(tokenSymbol: TokenSymbol): Wei {
  const transferable = useTotalTransferableAvailableMarginValue()
  const price = useExternalPriceByName(tokenSymbol)
  const currency = useCurrencyByName(tokenSymbol)
  const balance = useCurrencyBalance(tokenSymbol)

  return useMemo(() => {
    const { conversionRatio } = currency ?? {}
    const { available = '0' } = balance ?? {}
    // If the conversion ratio is 0, then the currency is not marginable and the available balance is the same as the balance
    if (conversionRatio === 0) {
      return available
    }

    if (conversionRatio === undefined) return '0'
    if (isZero(price)) return '0'
    if (isZero(conversionRatio) || isZero(available)) return available

    const v = bn(available)
      .mul(e2w(price))
      .mul(e2w(conversionRatio))
      .div(Pow18)
      .div(Pow18)
    const r = v.lte(transferable) ? v : transferable
    return r
      .mul(Pow18)
      .mul(Pow18)
      .div(e2w(price))
      .div(e2w(conversionRatio))
      .toString()
  }, [transferable, balance, price, currency])
}
